import React, { useState, useRef, useLayoutEffect, useContext } from 'react'
import cn from 'classnames'

import { useIsSectionThemeDark } from '../../../../contexts/section-theme-context'
import { PageContext } from '../../../../contexts/page-context'
import { PlacementContext } from '../../../../contexts/placement-context'
import { TypeSectionLinkableBoxes } from '../../../../lib/types'
import { ExternalLink } from '../../../ui-lib/link'
import { LinkableBoxItem } from '../../../item/linkable-box'
import { ContentProps } from '../../../renderer'
import { Chevron, ChevronDirection } from '../../../ui-lib/icons/chevron'
import { IconButton } from '../../../ui-lib/button'
import { gnarSpec } from '../../../../lib/tracking/gnar'

import styles from './scrollable-content.module.scss'

export const LinkableBoxesSectionScrollableContent: React.FC<ContentProps<TypeSectionLinkableBoxes>> = ({
  structure,
}) => {
  const {
    fields: { itemLinkableBox = [], isMatchingSectionBackground },
  } = structure

  const { pageId, slug } = useContext(PageContext)
  const isDarkTheme = useIsSectionThemeDark()
  const { placement, placementInternalName } = useContext(PlacementContext)

  // Calculate the initial padding for scroll, so that the start of first linkable box aligns vertically with the other sections.
  const [paddingLeft, setPaddingLeft] = useState(0)

  // finalSectionWidth is the scrollable section width that is the window width.
  // We need to set this here to override our page padding and auto margin.
  const [finalSectionWidth, setFinalSectionWidth] = useState(0)

  const [isAtScrollEnd, setIsAtScrollEnd] = useState(false)
  const [isAtScrollStart, setIsAtScrollStart] = useState(false)
  const scrollableSectionRef = useRef<HTMLDivElement>(null)

  const [currCardIdx, setCurrCardIdx] = useState(0)

  // We only need to offset the scroll by the using the prev card or the next cards position (depending on scroll direction)
  const prevCardRef = useRef<HTMLDivElement>(null)
  const nextCardRef = useRef<HTMLDivElement>(null)

  // We check whether the scroll is it's start position or whether the end position is in viewport
  // to enable/disable arrow buttons
  const checkScrollStartOrEnd = () => {
    const scrollSection = scrollableSectionRef.current

    if (scrollSection) {
      // Check whether the end of scroll is in viewport
      if (scrollSection.scrollLeft + scrollSection.clientWidth >= scrollSection.scrollWidth) {
        setIsAtScrollEnd(true)
      } else {
        setIsAtScrollEnd(false)
      }

      // Check whether the scroll is at the start. Add 1px buffer for card border
      if (Math.floor(scrollSection.scrollLeft) <= 2) {
        setIsAtScrollStart(true)
      } else {
        setIsAtScrollStart(false)
      }
    }
  }

  const scrollNextCardIn = () => {
    const scrollSection = scrollableSectionRef.current
    // Align the scroll position to make the next card snap into correct scroll position
    if (scrollSection && !isAtScrollEnd && nextCardRef.current?.offsetLeft) {
      scrollSection.scrollTo({
        left: nextCardRef.current?.offsetLeft - paddingLeft,
        behavior: 'smooth',
      })
      setCurrCardIdx((idx) => (idx !== itemLinkableBox.length - 1 ? idx + 1 : idx))
    }
  }

  const scrollToPrevCard = () => {
    const scrollSection = scrollableSectionRef.current
    if (scrollSection) {
      // There can be an edge case where the cardIdx was never updated because the user used mouse scroll instead of arrow btns.
      // In this case, if the user clicks on the previous arrow btn, scroll them to the start of the scroll.
      if (!prevCardRef.current?.offsetLeft) {
        scrollSection.scrollTo({
          left: 0,
          behavior: 'smooth',
        })
      } else {
        // As long as the prev card exists, align the scroll position to make the prev card snap into correct scroll position
        scrollSection.scrollTo({
          left: prevCardRef.current?.offsetLeft - paddingLeft,
          behavior: 'smooth',
        })
        setCurrCardIdx((idx) => (idx !== 0 ? idx - 1 : idx))
      }
    }
  }

  const onPrevClick = () => {
    gnarSpec.scrollableCardsNavButtonClick('previous', pageId, slug, placement, placementInternalName)
    scrollToPrevCard()
  }

  const onNextClick = () => {
    gnarSpec.scrollableCardsNavButtonClick('next', pageId, slug, placement, placementInternalName)
    scrollNextCardIn()
  }

  useLayoutEffect(() => {
    const handlePositioning = () => {
      if (scrollableSectionRef.current) {
        // Get parent's left value (has exact padding and margin which takes care of all screen sizes)
        const { left } = scrollableSectionRef.current.parentElement?.getBoundingClientRect() || {}
        if (left && window) {
          const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
          setPaddingLeft(left + scrollbarWidth)
          setFinalSectionWidth(window.innerWidth)
        }

        // Check initial scroll positions for arrow nav disable
        checkScrollStartOrEnd()
      }
    }

    handlePositioning()

    window.addEventListener('resize', handlePositioning)

    return () => {
      window.removeEventListener('resize', handlePositioning)
    }
  }, [])

  return (
    <div className={styles.scrollableSectionWrapper}>
      <div
        ref={scrollableSectionRef}
        className={styles.scrollableSection}
        style={{ paddingLeft, left: -paddingLeft, width: finalSectionWidth }}
        onScroll={checkScrollStartOrEnd}
        id="testimonial-scrollable-section"
        aria-label="Testiminial carousel"
        role="group"
      >
        {itemLinkableBox.map((linkableBox, i) => {
          const { targetUrl } = linkableBox.fields.link.fields
          const ariaLabel = linkableBox.fields?.content?.fields?.description
          return (
            <ExternalLink
              tabIndex={0}
              aria-label={ariaLabel}
              key={i}
              href={targetUrl}
              className={cn({
                [styles.cardLinkWrapper]: !isMatchingSectionBackground,
                [styles.cardLinkWrapperRedesigned24]: isMatchingSectionBackground,
                [styles.darkTheme]: isDarkTheme,
              })}
              onClick={() => {
                void gnarSpec.linkClick(pageId, slug, targetUrl, `cardIndex_${i}`, placement, placementInternalName)
              }}
            >
              <div
                key={i}
                tabIndex={-1}
                className={styles.card}
                ref={
                  i === currCardIdx - 1
                    ? prevCardRef
                    : i !== itemLinkableBox.length && i === currCardIdx + 1
                      ? nextCardRef
                      : null
                }
              >
                <LinkableBoxItem structure={linkableBox} />
              </div>
            </ExternalLink>
          )
        })}
      </div>
      <div
        role="group"
        aria-label="Testimonial carousel controls"
        className={cn({
          [styles.arrowNav]: !isMatchingSectionBackground,
          [styles.arrowNavRedesigned24]: isMatchingSectionBackground,
        })}
      >
        <IconButton
          onClick={onPrevClick}
          aria-label="Previous"
          aria-controls="testimonial-scrollable-section"
          className={cn(styles.navBtn, styles.prev, {
            [styles.darkTheme]: isDarkTheme,
          })}
          disabled={isAtScrollStart}
        >
          <Chevron
            direction={ChevronDirection.left}
            width={'20'}
            height={'20'}
            color={
              isAtScrollStart
                ? 'neutral30'
                : isDarkTheme
                  ? 'neutral0'
                  : isMatchingSectionBackground
                    ? 'neutral80'
                    : 'green60'
            }
            strokeWidth="1.5"
          />
        </IconButton>
        <IconButton
          onClick={onNextClick}
          aria-label="Next"
          aria-controls="testimonial-scrollable-section"
          className={cn(styles.navBtn, styles.next, {
            [styles.darkTheme]: isDarkTheme,
          })}
          disabled={isAtScrollEnd || currCardIdx === itemLinkableBox.length}
        >
          <Chevron
            width={'20'}
            height={'20'}
            color={
              isAtScrollEnd || currCardIdx === itemLinkableBox.length
                ? 'neutral30'
                : isDarkTheme
                  ? 'neutral0'
                  : isMatchingSectionBackground
                    ? 'neutral80'
                    : 'green60'
            }
            strokeWidth="1.5"
          />
        </IconButton>
      </div>
    </div>
  )
}
